import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@shared/analytics';
import { buildUnitPath } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, ChevronRightIcon, LibraryIcon, PlainField, PlusIcon } from '@shared/ui';
import type { DayPlan } from '@shared/types';
import { lineStatus, useTrainingPlans } from './moduleTraining';

interface WordsToolbarProps {
  /** Выбранный модуль; null — модулей нет. */
  module: { id: string; title: string } | null;
  /** Сколько карточек в модуле — из списка модулей страницы. */
  cards: number | null;
  /** Сводка по всем модулям — прогресс, когда модуля нет. */
  overall: DayPlan | null;
  search: string;
  onSearch: (value: string) => void;
  /** «Готовые наборы» — в плашке, а не под списком модулей (владелец 2026-10-28: «а то её не видно»). */
  onOpenReady: () => void;
  onCreateModule: () => void;
}

/** Кольцо «пройдено из»: доля пройденных слов модуля, в середине — «1/22». */
function ProgressRing({ passed, total, label }: { passed: number; total: number; label: string }) {
  const radius = 15;
  const length = 2 * Math.PI * radius;
  const share = total > 0 ? Math.min(1, passed / total) : 0;
  const text = `${passed}/${total}`;
  return (
    <span role="img" aria-label={label} className="relative grid h-[54px] w-[54px] flex-none place-items-center">
      <svg viewBox="0 0 36 36" aria-hidden className="absolute inset-0 h-full w-full -rotate-90">
        <circle cx="18" cy="18" r={radius} fill="none" stroke="#2a2a30" strokeWidth="4" />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="#F36A1C"
          strokeWidth="4"
          // Ноль пройденных — без точки от скруглённого конца.
          strokeLinecap={share > 0 ? 'round' : 'butt'}
          strokeDasharray={length}
          strokeDashoffset={length * (1 - share)}
          className="transition-[stroke-dashoffset] duration-500 motion-reduce:transition-none"
        />
      </svg>
      <span aria-hidden className={cn('relative font-bold tabular-nums text-fg', text.length > 6 ? 'text-[9.5px]' : 'text-[11px]')}>
        {text}
      </span>
    </span>
  );
}

/**
 * Плашка над колонками на компьютере (владелец 2026-10-28: «десктоп выглядит как будто ты в кабине самолёта»; из
 * четырёх вариантов выбран B — кольцо прогресса, «Открыть модуль» словами). Слева — кольцо «пройдено из», имя модуля
 * с «Открыть модуль ›», под ним — что повторить и сколько карточек; справа — поиск слова в модуле, «Готовые наборы» и
 * «Создать модуль». На узком компьютере правая часть уходит второй строкой.
 */
export function WordsToolbar({ module, cards, overall, search, onSearch, onOpenReady, onCreateModule }: WordsToolbarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plans = useTrainingPlans();
  const plan = module ? plans.get(module.id) : overall ?? undefined;
  const ready = plan?.ready ?? 0;
  const passed = plan ? Math.max(0, plan.ready - plan.new) : 0;
  const status = plan ? lineStatus(plan, t) : null;

  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-card border border-solid border-line bg-card py-3 pl-3.5 pr-3">
      <div className="flex min-w-[280px] flex-[1_1_280px] items-center gap-3.5">
        <ProgressRing passed={passed} total={ready} label={t('words.progress.line', { passed, count: ready })} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-baseline gap-3">
            <span className="min-w-0 truncate font-serif text-[22px] font-medium leading-[28px] tracking-[-0.01em] text-fg">
              {module ? module.title : t('words.toolbar.all')}
            </span>
            {module && (
              <button
                type="button"
                onClick={() => {
                  trackEvent('words_module_page_opened');
                  navigate(buildUnitPath(module.id));
                }}
                className="inline-flex flex-none items-center gap-0.5 text-[14px] font-semibold text-accent-hi outline-none hover:text-accent focus-visible:ring-2 focus-visible:ring-accent"
              >
                {t('words.list.openModule')}
                <ChevronRightIcon aria-hidden className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[13px] leading-[18px] text-muted">
            {plan && ready === 0 ? (
              <span className="min-w-0 truncate">{t('words.toolbar.empty')}</span>
            ) : (
              status && <span className={cn('min-w-0 truncate', status.repeat && 'text-accent-hi')}>{status.text}</span>
            )}
            {cards !== null && ready > 0 && (
              <span className="flex-none">{`· ${t('training.cardCount', { count: cards })}`}</span>
            )}
          </div>
        </div>
      </div>
      {/* Правая часть — по своему содержимому (имени модуля — остальное место); не влезает — второй строкой, а там
          сжимается поиск. */}
      <div className="ml-auto flex max-w-full flex-[0_1_auto] items-center justify-end gap-2">
        <PlainField
          value={search}
          onChange={onSearch}
          placeholder={module ? t('words.toolbar.searchIn') : t('words.list.search')}
          ariaLabel={module ? t('words.toolbar.searchIn') : t('words.list.search')}
          maxLength={64}
          enterKeyHint="search"
          className="w-[240px] min-w-[140px] shrink rounded-pill border border-line bg-base px-4 py-2 text-[15px] leading-6 text-fg outline-none transition-colors focus:border-accent"
        />
        <Button variant="pill-dark" size="sm" onClick={onOpenReady} className="inline-flex flex-none items-center gap-1.5">
          <LibraryIcon className="h-4 w-4" aria-hidden />
          {t('words.shelf.ready')}
        </Button>
        <Button variant="outline" size="sm" onClick={onCreateModule} className="inline-flex flex-none items-center gap-1.5">
          <PlusIcon className="h-4 w-4" aria-hidden />
          {t('units.createModule')}
        </Button>
      </div>
    </div>
  );
}
